import React, { PropsWithChildren } from 'react'

import { shallowEqual, useSelector } from 'react-redux'

import { EMPTY_OBJECT } from '@constants'
import { IApplicationState } from '@store'
import { selectDraftTrackingProps } from '@store/analytics'
import {
  selectCampaignTrackingProps,
  selectMediaObjectTrackingProps,
  selectSubmissionTrackingProps,
} from '@store/analytics'
import TrackedRoute, { IBaseRouteProps } from './TrackedRoute'

export interface IConnectedTrackedRouteIDProps {
  campaignId?: number
  mediaObjectId?: number
  submissionId?: number
  draftId?: number
}

export interface IConnectedTrackedRoute extends IConnectedTrackedRouteIDProps, IBaseRouteProps {
  additionalProperties?: object
}

const getBaseProperties = ({
  mediaObjectId,
  campaignId,
  submissionId,
  draftId,
  campaignAnalyticsProperties,
  submissionAnalyticsProperties,
  mediaObjectAnalyticsProperties,
  draftAnalyticsProperties,
}) => {
  if (draftId) {
    return draftAnalyticsProperties
  }
  if (submissionId) {
    return submissionAnalyticsProperties
  }
  if (!submissionId && mediaObjectId) {
    return mediaObjectAnalyticsProperties
  }
  if (campaignId) {
    return campaignAnalyticsProperties
  }
  return undefined
}

export const ConnectedTrackedRoute: React.FC<PropsWithChildren<IConnectedTrackedRoute>> = ({
  eventName,
  mediaObjectId,
  campaignId,
  submissionId,
  draftId,
  additionalProperties,
  targetLink,
  children,
  inline,
  disableTracking,
  ...trackedRouteProps
}) => {
  const {
    draftAnalyticsProperties,
    campaignAnalyticsProperties,
    submissionAnalyticsProperties,
    mediaObjectAnalyticsProperties,
  } = useSelector(
    (state: IApplicationState) => ({
      // Stable EMPTY_OBJECT fallbacks: inline `{}` produced a fresh reference each
      // render, defeating shallowEqual and causing re-renders on every store change.
      draftAnalyticsProperties: draftId ? selectDraftTrackingProps(state) : EMPTY_OBJECT,
      campaignAnalyticsProperties: campaignId
        ? selectCampaignTrackingProps(state, campaignId)
        : EMPTY_OBJECT,
      submissionAnalyticsProperties:
        campaignId && submissionId
          ? selectSubmissionTrackingProps(state, campaignId, submissionId)
          : EMPTY_OBJECT,
      mediaObjectAnalyticsProperties: mediaObjectId
        ? selectMediaObjectTrackingProps(state, mediaObjectId)
        : EMPTY_OBJECT,
    }),
    shallowEqual,
  )

  const baseProperties = getBaseProperties({
    mediaObjectId,
    campaignId,
    submissionId,
    draftId,
    campaignAnalyticsProperties,
    submissionAnalyticsProperties,
    mediaObjectAnalyticsProperties,
    draftAnalyticsProperties,
  })

  const properties = {
    ...baseProperties,
    ...additionalProperties,
  }

  return (
    <TrackedRoute
      eventName={eventName}
      targetLink={targetLink}
      inline={inline}
      properties={properties}
      disableTracking={disableTracking}
      {...trackedRouteProps}
    >
      {children}
    </TrackedRoute>
  )
}

export default ConnectedTrackedRoute
